import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import MainLayout from '../../components/layout/MainLayout';
import { Globe, Loader2, Search, User, UserX, X } from 'lucide-react';
import { auditApi } from '../../services/api';
import { toast } from 'sonner';

const fmt = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return iso; }
};

const shortId = (id) => (id ? `${id.slice(0, 8)}…` : '—');

const VisitorActivity = () => {
  const [visitors, setVisitors] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [vType, setVType] = useState('all');
  const [search, setSearch] = useState('');
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      try {
        const params = ['limit=100'];
        if (vType !== 'all') params.push(`visitor_type=${vType}`);
        if (search) params.push(`search=${encodeURIComponent(search)}`);
        const data = await auditApi.getVisitors(params.join('&'));
        if (active) { setVisitors(data.items); setTotal(data.total); }
      } catch (err) {
        toast.error(err.message || 'Failed to load visitors');
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => { active = false; };
  }, [vType, search]);

  const openDetail = async (visitorId) => {
    setDetailLoading(true);
    setDetail({ loading: true });
    try {
      const data = await auditApi.getVisitor(visitorId);
      setDetail(data);
    } catch (err) {
      toast.error(err.message || 'Failed to load visitor detail');
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  return (
    <MainLayout title="Visitor Activity" subtitle="Anonymous & registered visitor intelligence">
      <div data-testid="visitor-activity-page">
        <div className="flex items-center justify-between mb-6">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text" placeholder="Search visitor id / email..." value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-12 pr-4 py-3 w-80 rounded-xl input-glass" data-testid="visitor-search"
            />
          </div>
          <div className="flex items-center gap-2">
            <Globe className="w-4 h-4 text-gray-400" />
            <select value={vType} onChange={(e) => setVType(e.target.value)}
              className="px-4 py-2 rounded-xl glass-card bg-transparent outline-none" data-testid="visitor-filter-type">
              <option value="all">All Visitors ({total})</option>
              <option value="registered">Registered</option>
              <option value="anonymous">Anonymous</option>
            </select>
          </div>
        </div>

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="glass-card overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-20" data-testid="visitor-loading">
              <Loader2 className="w-7 h-7 animate-spin text-blue-500" />
            </div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="bg-gradient-to-r from-[#1E3A5F] to-[#2D5A8B] text-white">
                  <th className="px-6 py-4 text-left">Visitor</th>
                  <th className="px-6 py-4 text-left">Type</th>
                  <th className="px-6 py-4 text-left">Page Views</th>
                  <th className="px-6 py-4 text-left">First Seen</th>
                  <th className="px-6 py-4 text-left">Last Seen</th>
                  <th className="px-6 py-4 text-left">IP</th>
                </tr>
              </thead>
              <tbody data-testid="visitor-table-body">
                {visitors.length === 0 ? (
                  <tr><td colSpan={6} className="px-6 py-12 text-center text-gray-500">No visitors tracked yet.</td></tr>
                ) : visitors.map((v, i) => (
                  <motion.tr
                    key={v.visitor_id}
                    data-testid={`visitor-row-${v.visitor_id}`}
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.02 }}
                    onClick={() => openDetail(v.visitor_id)}
                    className="border-b border-gray-500/10 hover:bg-blue-500/5 cursor-pointer"
                  >
                    <td className="px-6 py-4 text-sm">
                      <div className="flex items-center gap-2">
                        {v.is_registered ? <User className="w-4 h-4 text-green-500" /> : <UserX className="w-4 h-4 text-gray-400" />}
                        <span>{v.is_registered ? (v.user_email || 'Registered') : 'Anonymous'}</span>
                      </div>
                      <span className="text-xs text-gray-400 font-mono">{shortId(v.visitor_id)}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 text-xs rounded-full ${v.is_registered ? 'bg-green-500/20 text-green-500' : 'bg-gray-500/20 text-gray-400'}`}>
                        {v.is_registered ? 'Registered' : 'Anonymous'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm">{v.page_view_count ?? 0}</td>
                    <td className="px-6 py-4 text-sm text-gray-500 whitespace-nowrap">{fmt(v.first_seen)}</td>
                    <td className="px-6 py-4 text-sm text-gray-500 whitespace-nowrap">{fmt(v.last_seen)}</td>
                    <td className="px-6 py-4 text-sm text-gray-500 font-mono">{(v.ip_addresses || [])[0] || '—'}</td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          )}
        </motion.div>

        <AnimatePresence>
          {detail && (
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
              onClick={() => setDetail(null)}
            >
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
                className="glass-card p-6 w-full max-w-2xl max-h-[85vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()} data-testid="visitor-detail-modal"
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold">Visitor Detail</h3>
                  <button onClick={() => setDetail(null)} className="p-2 rounded-lg hover:bg-gray-500/10" data-testid="visitor-detail-close"><X className="w-5 h-5" /></button>
                </div>
                {detailLoading || detail.loading ? (
                  <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-blue-500" /></div>
                ) : (
                  <div className="space-y-5">
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div><span className="text-gray-500">Visitor ID:</span> <span className="font-mono">{detail.visitor?.visitor_id}</span></div>
                      <div><span className="text-gray-500">Type:</span> {detail.visitor?.is_registered ? 'Registered' : 'Anonymous'}</div>
                      <div><span className="text-gray-500">Email:</span> {detail.visitor?.user_email || '—'}</div>
                      <div><span className="text-gray-500">Events:</span> {detail.visitor?.event_count ?? 0}</div>
                      <div><span className="text-gray-500">Referrer:</span> {detail.visitor?.referrer || '—'}</div>
                      <div><span className="text-gray-500">IPs:</span> {(detail.visitor?.ip_addresses || []).join(', ') || '—'}</div>
                    </div>
                    <div>
                      <h4 className="font-semibold text-sm mb-2">Sessions ({detail.sessions?.length || 0})</h4>
                      <div className="space-y-2">
                        {(detail.sessions || []).map(s => (
                          <div key={s.session_id} className="p-3 rounded-lg bg-gray-500/5 text-xs flex justify-between">
                            <span className="font-mono">{shortId(s.session_id)}</span>
                            <span className="text-gray-500">{s.page_count}pv · {s.duration_seconds}s · {fmt(s.start)}</span>
                          </div>
                        ))}
                        {(!detail.sessions || detail.sessions.length === 0) && <p className="text-xs text-gray-500">No sessions.</p>}
                      </div>
                    </div>
                    <div>
                      <h4 className="font-semibold text-sm mb-2">Recent Events ({detail.events?.length || 0})</h4>
                      <div className="space-y-1 max-h-48 overflow-y-auto">
                        {(detail.events || []).map(ev => (
                          <div key={ev.id} className="p-2 rounded bg-gray-500/5 text-xs flex justify-between">
                            <span><span className="text-blue-400">{ev.event_type}</span> {ev.path || ''}</span>
                            <span className="text-gray-500 whitespace-nowrap ml-2">{fmt(ev.timestamp)}</span>
                          </div>
                        ))}
                        {(!detail.events || detail.events.length === 0) && <p className="text-xs text-gray-500">No events.</p>}
                      </div>
                    </div>
                  </div>
                )}
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </MainLayout>
  );
};

export default VisitorActivity;
